// Projeto: Haklai Church — Plugin WordPress Modular - Desenvolvido por: Ricardo Sarmento - https://linx.pt

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';

// Leaflet
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { initLeafletIcons } from '../utils/leafletSetup';

function MapCellsPage() {
    initLeafletIcons();

    const navigate = useNavigate();

    const { data: listResponse, isLoading } = useQuery({
        queryKey: ['cells'],
        queryFn: async () => {
            const response = await api.get('/cells', { params: { per_page: 999 } });
            return response.data;
        },
    });

    const cells = Array.isArray(listResponse?.data) ? listResponse.data : [];

    // Filtrar apenas células que possuem latitude e longitude válidas
    const mappedCells = cells.filter(cell => {
        if (!cell.is_active || !cell.latitude || !cell.longitude) return false;
        const lat = parseFloat(cell.latitude);
        const lng = parseFloat(cell.longitude);
        return !isNaN(lat) && !isNaN(lng);
    });

    // Calcula centro do mapa baseado nas células (ou usa default se vazio)
    const defaultCenter = [38.7223, -9.1393]; // Lisboa como fallback inicial

    let mapCenter = defaultCenter;
    if (mappedCells.length > 0) {
        const validCells = mappedCells.map(c => ({ lat: parseFloat(c.latitude), lng: parseFloat(c.longitude) }));
        const sumLat = validCells.reduce((sum, cell) => sum + cell.lat, 0);
        const sumLng = validCells.reduce((sum, cell) => sum + cell.lng, 0);
        mapCenter = [sumLat / validCells.length, sumLng / validCells.length];
    }

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            zIndex: 999999, // Cobre os elementos do admin do WordPress
            backgroundColor: 'var(--background, #f6f7f8)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            fontFamily: 'var(--font-family, sans-serif)'
        }}>
            {/* Header do Mapa em Tela Inteira */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '1rem 1.5rem',
                backgroundColor: 'var(--surface, #ffffff)',
                borderBottom: '1px solid var(--border, #dfe1e6)',
                boxShadow: 'var(--shadow-sm, 0 1px 3px rgba(0, 0, 0, 0.08))',
                zIndex: 10,
                position: 'relative'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <button 
                        className="btn btn-secondary" 
                        onClick={() => navigate('/dashboard')} 
                        style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem', borderRadius: 'var(--radius-md, 8px)', height: 'fit-content' }}
                    >
                        <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>arrow_back</span>
                        Voltar
                    </button>
                    <div>
                        <h1 style={{ fontSize: '1.25rem', fontWeight: 'var(--font-weight-bold, 700)', color: 'var(--text-primary, #172b4d)', margin: 0 }}>
                            Mapa de Células
                        </h1>
                        <p style={{ color: 'var(--text-muted, #97a0af)', fontSize: '0.875rem', margin: 0 }}>
                            {isLoading ? 'A carregar localizações...' : `Visualizando ${mappedCells.length} células ativas no mapa.`}
                        </p>
                    </div>
                </div>
                <button 
                    className="btn btn-primary" 
                    onClick={() => navigate('/cells/new')}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem', borderRadius: 'var(--radius-md, 8px)' }}
                >
                    <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>add</span>
                    Nova Célula
                </button>
            </div>

            {isLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                    <LoadingSpinner size="lg" />
                </div>
            ) : (
                <div style={{ flex: 1, position: 'relative', width: '100%', height: '100%' }}>
                    {mappedCells.length === 0 ? (
                        <div style={{
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            backgroundColor: 'var(--background-alt, #ebeef1)',
                            color: 'var(--text-secondary, #6b778c)'
                        }}>
                            <span className="material-symbols-outlined" style={{ fontSize: '3rem', color: 'var(--text-muted, #97a0af)', marginBottom: '1rem' }}>
                                satellite_alt
                            </span>
                            <p style={{ fontSize: '1rem', fontWeight: '500' }}>Nenhuma célula com localização no mapa encontrada.</p>
                            <button className="btn btn-outline" style={{ marginTop: '1rem', border: '1px solid var(--border, #dfe1e6)', padding: '0.5rem 1rem', borderRadius: 'var(--radius-md, 8px)' }} onClick={() => navigate('/cells')}>
                                Ver Lista de Células
                            </button>
                        </div>
                    ) : (
                        <MapContainer
                            key={mapCenter.join(',')}
                            center={mapCenter}
                            zoom={11}
                            style={{ height: '100%', width: '100%', zIndex: 1 }}
                        >
                            <TileLayer
                                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            />

                            {mappedCells.map(cell => (
                                <Marker
                                    key={cell.id}
                                    position={[parseFloat(cell.latitude), parseFloat(cell.longitude)]}
                                >
                                    <Popup>
                                        <div style={{ padding: '0.5rem', minWidth: '150px' }}>
                                            <h3 style={{ margin: '0 0 0.5rem 0', fontWeight: 'bold', fontSize: '1rem', color: 'var(--text-primary, #172b4d)' }}>{cell.name}</h3>
                                            {cell.address && <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.875rem', color: 'var(--text-secondary, #6b778c)' }}>{cell.address}</p>}

                                            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
                                                <button
                                                    className="btn btn-sm btn-primary"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigate(`/cells/${cell.id}`);
                                                    }}
                                                    style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', width: '100%' }}
                                                >
                                                    Ver Detalhes
                                                </button>
                                            </div>
                                        </div>
                                    </Popup>
                                </Marker>
                            ))}
                        </MapContainer>
                    )}
                </div>
            )}
        </div>
    );
}

export default MapCellsPage;

